import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useSeoAbVariant } from '@/hooks/useSeoAbVariant';
import { 
  Wrench, Scale, Stethoscope, Building2, Users, Briefcase,
  ArrowRight, Calendar, Target, Clock,
  CheckCircle, Star, MessageSquare, DollarSign, TrendingUp,
  Shield, Zap, ChevronDown, Bot
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PublicHeader } from '@/components/common/PublicHeader';
import { PublicFooter } from '@/components/common/PublicFooter';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const INDUSTRY_IMAGES = {
  plumbers: 'https://images.unsplash.com/photo-1599463698367-11cb72775b67?w=800&h=500&fit=crop',
  lawyers: 'https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?w=800&h=500&fit=crop',
  clinics: 'https://images.unsplash.com/photo-1758691462126-2ee47c8bf9e7?w=800&h=500&fit=crop',
  'real-estate': 'https://images.unsplash.com/photo-1756435292384-1bf32eff7baf?w=800&h=500&fit=crop',
  consultants: 'https://images.unsplash.com/photo-1765020553734-2c050ddb9494?w=800&h=500&fit=crop',
  agencies: 'https://images.unsplash.com/photo-1666698809123-44e998e93f23?w=800&h=500&fit=crop',
  dental: 'https://images.unsplash.com/photo-1642844819197-5f5f21b89ff8?w=800&h=500&fit=crop',
  hvac: 'https://images.unsplash.com/photo-1545280405-f06710f1779d?w=800&h=500&fit=crop',
  auto: 'https://images.unsplash.com/photo-1619642737579-a7474bee1044?w=800&h=500&fit=crop',
  salons: 'https://images.unsplash.com/photo-1763048208932-cbe149724374?w=800&h=500&fit=crop',
  fitness: 'https://images.unsplash.com/photo-1771586791190-97ed536c54af?w=800&h=500&fit=crop',
};

// Industry data with SEO-optimized content
const industriesData = {
  plumbers: {
    name: 'Plumbing Businesses',
    headline: 'AI Assistant for Plumbing Businesses',
    subheadline: 'Never Miss a Lead Again. Let AI Handle Your Messages 24/7.',
    icon: Wrench,
    color: 'from-teal-600 to-cyan-600',
    metaDescription: 'AI assistant for plumbers. Handle emergency inquiries, book service appointments, and capture leads 24/7. Start free trial.',
    painPoints: [
      { title: 'Missed Messages', description: 'After-hours inquiries mean lost revenue. Every unanswered message is a customer messaging your competitor.' },
      { title: 'After-Hours Inquiries', description: 'Customers need help at 2 AM when pipes burst. Your AI employee never sleeps.' },
      { title: 'Manual Scheduling Chaos', description: 'Double bookings, missed appointments, and scheduling conflicts waste technician time.' },
      { title: 'Lead Follow-up Delays', description: 'Hot leads go cold when you can\'t respond within minutes. AI responds instantly.' }
    ],
    useCases: [
      { title: 'Emergency Intake Automation', description: 'AI qualifies emergencies, captures location and problem details, and sends alerts to on-call technicians.' },
      { title: 'Service Area Qualification', description: 'Automatically verify if the customer is within your service area before booking appointments.' },
      { title: 'Availability-Based Booking', description: 'Real-time calendar sync ensures customers only see available time slots for their service type.' },
      { title: 'Follow-up Reminders', description: 'Automated appointment reminders reduce no-shows by 60%. AI sends text and email confirmations.' }
    ],
    sampleConversation: [
      { role: 'customer', message: "Help! My water heater is leaking everywhere!" },
      { role: 'ai', message: "I understand this is urgent - a leaking water heater needs immediate attention. Let me help you right away. First, can you tell me: is there a shut-off valve on your water heater you can turn off? It's usually a lever or knob on the cold water line going into the tank." },
      { role: 'customer', message: "Yes, I turned it off. Water is still all over the floor though." },
      { role: 'ai', message: "Good thinking turning off the water supply. I'm notifying an emergency plumber to your location now. Can you confirm your address is 123 Main St? Also, is your water heater gas or electric? This helps our technician come prepared with the right equipment." }
    ],
    roiComparison: {
      traditional: { title: 'Hiring a Receptionist', cost: '$3,200/mo', items: ['$15-20/hr salary', 'Benefits & taxes', 'Only works 8 hours/day', 'Messages missed on breaks/sick days'] },
      harmonix: { title: 'Harmonix AI', cost: '$99/mo', items: ['24/7/365 availability', 'Never takes breaks', 'Handles unlimited messages', 'Instant response time'] }
    },
    faqs: [
      { q: 'Can the AI handle emergency plumbing requests?', a: 'Yes! Our AI is specifically trained to recognize emergencies, capture critical details (location, problem severity, shut-off status), and immediately notify your on-call technicians while keeping the customer informed.' },
      { q: 'Does it integrate with my scheduling software?', a: 'Harmonix integrates with Google Calendar, ServiceTitan, Housecall Pro, and most scheduling tools used by plumbing businesses. We can also work with custom solutions via API.' },
      { q: 'What happens if the AI can\'t answer a question?', a: 'The AI knows its limits. For complex technical questions or situations requiring human judgment, it will collect the customer\'s information and schedule a callback from your team.' },
      { q: 'Can it differentiate between emergency and routine requests?', a: 'Absolutely. Our AI asks qualifying questions to determine urgency. Emergencies (flooding, no water, gas smell) get immediate escalation, while routine maintenance gets scheduled appropriately.' }
    ],
    cta: 'Start Your AI Plumbing Assistant',
    stats: { messagesHandled: '50,000+', avgResponseTime: '3 seconds', customerSatisfaction: '94%' }
  },
  lawyers: {
    name: 'Law Firms',
    headline: 'AI Intake Specialist for Law Firms',
    subheadline: 'Professional Client Intake, 24/7. Never Lose a Potential Case Again.',
    icon: Scale,
    color: 'from-slate-700 to-slate-800',
    metaDescription: 'AI intake specialist for law firms. Qualify leads, capture case details, and schedule consultations 24/7. Attorney-approved responses.',
    painPoints: [
      { title: 'After-Hours Lead Loss', description: 'Accident victims call when incidents happen - often outside business hours. Lost leads mean lost cases.' },
      { title: 'Inconsistent Intake Quality', description: 'Different staff members ask different questions. Missing critical information delays case evaluation.' },
      { title: 'High Cost of Human Intake', description: 'Dedicated intake specialists cost $45-60K/year. Most firms can\'t afford 24/7 coverage.' },
      { title: 'Slow Response Times', description: 'Studies show leads contacted within 5 minutes are 9x more likely to convert. Can your team respond that fast?' }
    ],
    useCases: [
      { title: 'Legal Intake Automation', description: 'Structured intake questions capture case type, incident date, injuries, and other parties involved - exactly what attorneys need.' },
      { title: 'Practice Area Routing', description: 'AI identifies whether it\'s a PI, family law, criminal, or other matter and routes to the appropriate attorney or department.' },
      { title: 'Conflict Check Preparation', description: 'Captures all party names and relevant information needed for conflict checks before the consultation.' },
      { title: 'Consultation Scheduling', description: 'Books consultations directly into attorney calendars with automatic conflict detection.' }
    ],
    sampleConversation: [
      { role: 'customer', message: "I was in a car accident yesterday and I think I need a lawyer." },
      { role: 'ai', message: "I'm sorry to hear about your accident. We're here to help. To connect you with the right attorney, I need to gather some initial information. First, were you the driver, passenger, or pedestrian in this accident?" },
      { role: 'customer', message: "I was driving. The other driver ran a red light." },
      { role: 'ai', message: "Thank you for sharing that. I'm documenting that you were the driver and the other party allegedly ran a red light. Have you received any medical treatment for injuries from this accident? This is important for evaluating your case." }
    ],
    roiComparison: {
      traditional: { title: 'Intake Specialist', cost: '$4,500/mo', items: ['$50-60K annual salary', 'Training and turnover costs', 'Limited to business hours', 'Human error in documentation'] },
      harmonix: { title: 'Harmonix AI', cost: '$149/mo', items: ['24/7/365 intake coverage', 'Consistent question flow', 'Perfect documentation', 'Instant lead notification'] }
    },
    faqs: [
      { q: 'Is the AI compliant with legal ethics rules?', a: 'Yes. Our AI is carefully designed to avoid practicing law - it only gathers information and schedules consultations. All legal advice comes from licensed attorneys.' },
      { q: 'Can it handle confidential information securely?', a: 'Harmonix uses bank-level encryption and is SOC 2 compliant. We take attorney-client privilege seriously, even at the intake stage.' },
      { q: 'Does it work for different practice areas?', a: 'Yes. We support personal injury, family law, criminal defense, immigration, estate planning, and more. Each practice area has tailored intake flows.' },
      { q: 'What if a potential client has an urgent matter?', a: 'The AI recognizes urgency indicators (criminal arrests, protective orders, statute of limitations) and can immediately escalate to your emergency contact.' }
    ],
    cta: 'Deploy AI Intake for Your Law Firm',
    stats: { messagesHandled: '30,000+', avgResponseTime: '2 seconds', customerSatisfaction: '96%' }
  },
  clinics: {
    name: 'Medical Clinics',
    headline: 'AI Assistant for Medical Clinics',
    subheadline: 'HIPAA-Aware Patient Communication. Reduce No-Shows by 60%.',
    icon: Stethoscope,
    color: 'from-emerald-500 to-emerald-600',
    metaDescription: 'AI assistant for medical clinics. HIPAA-aware appointment scheduling, patient intake, and FAQ handling. Reduce administrative burden.',
    painPoints: [
      { title: 'Front Desk Overwhelm', description: 'Your reception staff juggles messages, check-ins, and paperwork. Patient experience suffers when they\'re spread too thin.' },
      { title: 'High No-Show Rates', description: 'Missed appointments cost healthcare providers $150B annually. Many could be prevented with better reminders.' },
      { title: 'After-Hours Patient Inquiries', description: 'Patients have questions at night and on weekends. Unanswered messages aren\'t a good patient experience.' },
      { title: 'Repetitive FAQ Burden', description: 'Staff answer the same questions hundreds of times: office hours, insurance accepted, directions.' }
    ],
    useCases: [
      { title: 'Appointment Scheduling', description: 'Patients book appointments based on real-time availability, provider preferences, and appointment type requirements.' },
      { title: 'Appointment Reminders', description: 'Automated SMS and email reminders with easy reschedule options reduce no-shows dramatically.' },
      { title: 'Pre-Visit Intake', description: 'Collect patient information, insurance details, and reason for visit before they arrive.' },
      { title: 'FAQ Automation', description: 'Answer common questions about office hours, accepted insurance, location, and preparation instructions.' }
    ],
    sampleConversation: [
      { role: 'customer', message: "Hi, I need to schedule a checkup with Dr. Smith." },
      { role: 'ai', message: "I'd be happy to help you schedule an appointment with Dr. Smith. I see availability this week on Thursday at 2:00 PM or Friday at 10:00 AM. Would either of those work for you?" },
      { role: 'customer', message: "Thursday works. Also, do you accept Blue Cross insurance?" },
      { role: 'ai', message: "Great! I've reserved Thursday at 2:00 PM for your checkup with Dr. Smith. And yes, we do accept Blue Cross Blue Shield plans. Please bring your insurance card to your appointment. Would you like me to send you a reminder text the day before?" }
    ],
    roiComparison: {
      traditional: { title: 'Additional Receptionist', cost: '$3,800/mo', items: ['$18-22/hr salary', 'Benefits required', '8 hours/day coverage', 'Training time needed'] },
      harmonix: { title: 'Harmonix AI', cost: '$129/mo', items: ['24/7 patient support', 'Instant responses', 'Never takes time off', '60% no-show reduction'] }
    },
    faqs: [
      { q: 'Is Harmonix HIPAA compliant?', a: 'Harmonix is designed with HIPAA considerations in mind. We use encrypted communications and don\'t store PHI beyond what\'s necessary for scheduling. We recommend reviewing with your compliance officer.' },
      { q: 'Can it integrate with our EHR system?', a: 'We integrate with many popular practice management systems including Epic, Cerner, athenahealth, and others via HL7/FHIR standards or custom API connections.' },
      { q: 'What if a patient describes a medical emergency?', a: 'The AI is trained to recognize emergency language and immediately directs patients to call 911 or go to the nearest ER while notifying your staff.' },
      { q: 'Can patients reschedule through the AI?', a: 'Yes! Patients can cancel or reschedule appointments 24/7. The AI offers alternative times and sends updated confirmations automatically.' }
    ],
    cta: 'Start Your AI Medical Assistant',
    stats: { messagesHandled: '80,000+', avgResponseTime: '2 seconds', customerSatisfaction: '95%' }
  },
  'real-estate': {
    name: 'Real Estate',
    headline: 'AI Assistant for Real Estate Agents',
    subheadline: 'Capture Every Lead. Qualify Buyers. Book Showings 24/7.',
    icon: Building2,
    color: 'from-amber-500 to-orange-500',
    metaDescription: 'AI assistant for real estate agents. Capture leads from listings, qualify buyers, and schedule showings automatically. Never miss a hot lead.',
    painPoints: [
      { title: 'Lead Response Delays', description: 'Zillow leads expect responses in minutes. If you\'re showing a property, that lead goes to another agent.' },
      { title: 'Tire Kicker Qualification', description: 'Spending hours with unqualified buyers wastes your most valuable resource: time.' },
      { title: 'Showing Scheduling Chaos', description: 'Coordinating schedules between buyers, sellers, and yourself is a logistical nightmare.' },
      { title: 'After-Hours Inquiries', description: 'The best leads often come at night when people browse listings. Morning callbacks mean cold leads.' }
    ],
    useCases: [
      { title: 'Instant Lead Response', description: 'When leads come in from Zillow, Realtor.com, or your website, AI responds in seconds with property details and qualification questions.' },
      { title: 'Buyer Qualification', description: 'AI asks about pre-approval status, budget, timeline, and must-haves to identify serious buyers.' },
      { title: 'Automated Showing Scheduling', description: 'Qualified buyers can book showings directly into your calendar. AI coordinates with seller availability.' },
      { title: 'Property Information', description: 'Answer questions about listings 24/7: square footage, HOA fees, school districts, and more.' }
    ],
    sampleConversation: [
      { role: 'customer', message: "I saw the listing on Oak Street. Is it still available?" },
      { role: 'ai', message: "Yes! The beautiful 4-bedroom home at 456 Oak Street is still available at $485,000. It features 2,400 sq ft, a renovated kitchen, and a backyard pool. Are you currently working with a real estate agent, or would you like to schedule a showing with our team?" },
      { role: 'customer', message: "Not yet. I'd love to see it this weekend if possible." },
      { role: 'ai', message: "I have availability on Saturday at 11 AM or Sunday at 2 PM. Which works better for you? Also, to ensure we find homes that match your needs, are you pre-approved for financing or working with a lender?" }
    ],
    roiComparison: {
      traditional: { title: 'Showing Assistant', cost: '$2,500/mo', items: ['Part-time assistant', 'Limited availability', 'Training required', 'Inconsistent follow-up'] },
      harmonix: { title: 'Harmonix AI', cost: '$99/mo', items: ['24/7 lead response', 'Instant qualification', 'Automated scheduling', 'Never misses a lead'] }
    },
    faqs: [
      { q: 'Does it integrate with my MLS?', a: 'Harmonix can pull listing data from major MLS systems and IDX feeds. Your AI always has current property information and availability.' },
      { q: 'Can it handle multiple listings at once?', a: 'Absolutely. Your AI knows all your listings and can field questions about any property, routing serious inquiries to you immediately.' },
      { q: 'What about buyer vs. seller leads?', a: 'The AI distinguishes between buyers and sellers, asking appropriate qualification questions for each and routing them through different workflows.' },
      { q: 'How does showing scheduling work?', a: 'AI checks your calendar availability, considers seller preferences and lockbox access, and books showings that work for everyone - all without your involvement.' }
    ],
    cta: 'Start Your AI Real Estate Assistant',
    stats: { messagesHandled: '45,000+', avgResponseTime: '5 seconds', customerSatisfaction: '93%' }
  },
  consultants: {
    name: 'Consultants',
    headline: 'AI Intake for Consultants & Coaches',
    subheadline: 'Scale Your Practice Without Scaling Your Admin Work.',
    icon: Users,
    color: 'from-purple-500 to-purple-600',
    metaDescription: 'AI intake assistant for consultants and coaches. Qualify prospects, book discovery sessions, and automate follow-ups. Focus on billable work.',
    painPoints: [
      { title: 'Discovery Session No-Shows', description: 'Prospects book sessions but don\'t show up. Your time is worth $200-500/hour - no-shows are expensive.' },
      { title: 'Unqualified Prospects', description: 'Spending 30 minutes with someone who can\'t afford your services wastes everyone\'s time.' },
      { title: 'Admin Overhead', description: 'You became a consultant to do consulting, not to manage calendars and chase leads.' },
      { title: 'Inconsistent Lead Flow', description: 'Feast or famine: too busy to market when you have clients, scrambling when you don\'t.' }
    ],
    useCases: [
      { title: 'Prospect Qualification', description: 'AI asks about budget, timeline, and specific challenges to ensure only qualified leads get on your calendar.' },
      { title: 'Discovery Session Booking', description: 'Qualified prospects book directly into your calendar with pre-session questionnaires completed.' },
      { title: 'Automated Reminders', description: 'Multiple touchpoints before sessions reduce no-shows and ensure prospects come prepared.' },
      { title: 'Lead Nurturing', description: 'Non-ready prospects get value-add follow-ups until they\'re ready to buy.' }
    ],
    sampleConversation: [
      { role: 'customer', message: "I'm interested in your business coaching services." },
      { role: 'ai', message: "Great to hear from you! To make sure I connect you with the right program, can you tell me a bit about your business? Specifically, what's your current annual revenue and what growth challenge are you looking to solve?" },
      { role: 'customer', message: "We're at about $500K and trying to break through to $1M but struggling with sales." },
      { role: 'ai', message: "That's a common growth plateau, and sales strategy is definitely something we help with. Our business coaching program has helped dozens of companies in your position. Would you like to schedule a complimentary 30-minute strategy session to discuss your specific situation? I have availability Tuesday at 10 AM or Wednesday at 3 PM." }
    ],
    roiComparison: {
      traditional: { title: 'Virtual Assistant', cost: '$1,500/mo', items: ['10-15 hours/week', 'Timezone limitations', 'Training required', 'Inconsistent availability'] },
      harmonix: { title: 'Harmonix AI', cost: '$79/mo', items: ['24/7 prospect engagement', 'Instant responses', 'Pre-qualification built-in', 'Automated follow-up'] }
    },
    faqs: [
      { q: 'Can it qualify based on my specific criteria?', a: 'Yes! You define what makes a qualified prospect - budget thresholds, company size, specific challenges - and the AI filters accordingly.' },
      { q: 'What if someone isn\'t ready to buy yet?', a: 'The AI can add them to a nurture sequence, sending value-add content and checking in periodically until they\'re ready to book a session.' },
      { q: 'Does it work with my booking software?', a: 'Harmonix integrates with Calendly, Acuity, Google Calendar, and most popular scheduling tools. Discovery sessions go directly to your calendar.' },
      { q: 'Can it handle group coaching inquiries?', a: 'Absolutely. The AI can explain different program options, pricing tiers, and route prospects to the appropriate offering based on their needs.' }
    ],
    cta: 'Deploy Your AI Intake Assistant',
    stats: { messagesHandled: '20,000+', avgResponseTime: '3 seconds', customerSatisfaction: '94%' }
  },
  agencies: {
    name: 'Marketing Agencies',
    headline: 'AI Assistant for Marketing Agencies',
    subheadline: 'White-Label AI for Your Clients. Scale Revenue Without Scaling Headcount.',
    icon: Briefcase,
    color: 'from-rose-500 to-rose-600',
    metaDescription: 'White-label AI assistant for marketing agencies. Offer AI receptionist services to clients. Multi-tenant platform with custom branding.',
    painPoints: [
      { title: 'Client Service Demands', description: 'Clients expect 24/7 responsiveness, but your team works 9-5. The gap creates churn risk.' },
      { title: 'Scaling Bottlenecks', description: 'Adding clients means adding headcount. Margins shrink as you grow.' },
      { title: 'Differentiation Challenges', description: 'Every agency offers SEO, PPC, and social. What makes you different?' },
      { title: 'Recurring Revenue Pressure', description: 'Project work is feast or famine. Monthly retainers provide stability but require ongoing value delivery.' }
    ],
    useCases: [
      { title: 'White-Label AI for Clients', description: 'Offer AI receptionist as a value-add service to clients. Your branding, your pricing, your relationship.' },
      { title: 'Multi-Tenant Management', description: 'Manage all client AI agents from one dashboard. Deploy, customize, and monitor at scale.' },
      { title: 'Lead Management Service', description: 'Bundle AI lead capture with your marketing services. Prove ROI with actual leads, not just impressions.' },
      { title: 'New Revenue Stream', description: 'Add $99-299/month per client for AI services with 70%+ margins. Scale without hiring.' }
    ],
    sampleConversation: [
      { role: 'customer', message: "Hi, I saw your client's ad for HVAC services. My AC isn't working." },
      { role: 'ai', message: "I'm sorry to hear your AC isn't working - I know how uncomfortable that can be! I can help you schedule a service call with Johnson HVAC right away. Are you experiencing no cooling at all, or is the unit running but not cooling effectively?" },
      { role: 'customer', message: "It's running but blowing warm air." },
      { role: 'ai', message: "That sounds like it could be a refrigerant or compressor issue - definitely something our technicians can diagnose. I have a service slot available today at 4 PM or tomorrow morning at 9 AM. Which works better for you? I'll need your address and phone number to confirm the appointment." }
    ],
    roiComparison: {
      traditional: { title: 'Hire Account Managers', cost: '$5,000/mo per 10 clients', items: ['$50-60K salaries', 'Training and turnover', 'Limited scalability', 'Inconsistent service'] },
      harmonix: { title: 'Harmonix Agency Plan', cost: '$499/mo unlimited clients', items: ['Unlimited client AI agents', 'White-label branding', 'Reseller margins', 'Instant deployment'] }
    },
    faqs: [
      { q: 'Is it truly white-label?', a: 'Yes! Your clients see your branding only. You set the pricing, manage the relationship, and keep the margins. We\'re invisible.' },
      { q: 'How do I manage multiple clients?', a: 'Our agency dashboard lets you deploy, customize, and monitor all client AI agents from one place. Add new clients in minutes.' },
      { q: 'What margins can I expect?', a: 'Agencies typically charge $199-499/month per client for AI services while paying a fraction of that. 70%+ margins are common.' },
      { q: 'Do you offer agency partner support?', a: 'Agency partners get priority support, co-marketing opportunities, and dedicated account management as you scale.' }
    ],
    cta: 'Become an Agency Partner',
    stats: { messagesHandled: '100,000+', avgResponseTime: '2 seconds', customerSatisfaction: '95%' }
  },
  dental: {
    name: 'Dental Practices',
    headline: 'AI Assistant for Dental Practices',
    subheadline: 'Book Appointments 24/7. Reduce No-Shows. Grow Your Practice.',
    icon: Stethoscope,
    color: 'from-cyan-500 to-teal-500',
    metaDescription: 'AI assistant for dental offices. Handle appointment booking, insurance questions, and patient intake 24/7. Start free trial.',
    painPoints: [
      { title: 'Missed Appointment Requests', description: 'When patients message during procedures or lunch, you miss new patient opportunities worth $500+ each.' },
      { title: 'High No-Show Rates', description: 'Empty chairs cost $200-400/hour. Manual reminder messages barely dent the 15-20% no-show rate.' },
      { title: 'Insurance Confusion', description: 'Front desk spends hours on insurance questions. Patients want instant answers about coverage.' },
      { title: 'After-Hours Emergencies', description: 'Dental emergencies don\'t wait for business hours. Lost emergency patients go to competitors.' }
    ],
    useCases: [
      { title: '24/7 Appointment Booking', description: 'Patients book cleanings, checkups, and procedures anytime. AI knows your schedule and procedure durations.' },
      { title: 'Smart Appointment Reminders', description: 'Automated SMS and email reminders reduce no-shows by 60%. Includes easy reschedule options.' },
      { title: 'Insurance Pre-Qualification', description: 'AI asks about insurance, captures plan details, and sets expectations before the visit.' },
      { title: 'Emergency Triage', description: 'After-hours, AI assesses urgency and either schedules emergency appointments or provides care instructions.' }
    ],
    sampleConversation: [
      { role: 'customer', message: "I have terrible tooth pain and need to see a dentist ASAP." },
      { role: 'ai', message: "I'm sorry you're in pain - dental emergencies are no fun. Let me help you right away. Can you describe the pain? Is it sharp, throbbing, or constant? And which tooth is affected?" },
      { role: 'customer', message: "It's a sharp pain on my lower right side, gets worse when I eat." },
      { role: 'ai', message: "That sounds like it could be a cavity or cracked tooth that needs attention. I have an emergency appointment available today at 3:30 PM. Would you like me to book that? I'll also note your symptoms so Dr. Smith can prepare." }
    ],
    roiComparison: {
      traditional: { title: 'Front Desk Staff', cost: '$3,500/mo', items: ['$17-22/hr wages', 'Benefits and training', 'Limited to office hours', 'Handling multiple tasks'] },
      harmonix: { title: 'Harmonix AI', cost: '$99/mo', items: ['24/7 booking capability', 'Automated reminders', 'Consistent patient experience', 'Never on hold'] }
    },
    faqs: [
      { q: 'Can it handle insurance verification?', a: 'The AI collects insurance information and sets realistic expectations. For detailed benefit verification, it prepares all info for your staff.' },
      { q: 'Does it integrate with dental software?', a: 'Harmonix integrates with Dentrix, Eaglesoft, Open Dental, and most practice management systems for real-time scheduling.' },
      { q: 'What about HIPAA compliance?', a: 'Our AI is designed with HIPAA in mind. We use encrypted communications and don\'t store protected health information unnecessarily.' },
      { q: 'Can it remind patients about cleanings?', a: 'Yes! The AI can proactively reach out to patients due for 6-month cleanings, helping you maintain a healthy recall schedule.' }
    ],
    cta: 'Start Your AI Dental Assistant',
    stats: { messagesHandled: '60,000+', avgResponseTime: '3 seconds', customerSatisfaction: '94%' }
  },
  hvac: {
    name: 'HVAC Companies',
    headline: 'AI Assistant for HVAC Companies',
    subheadline: 'Handle Emergency Requests 24/7. Book Service Appointments Automatically.',
    icon: Wrench,
    color: 'from-orange-500 to-red-500',
    metaDescription: 'AI assistant for HVAC companies. Handle AC emergencies, furnace repairs, and maintenance scheduling 24/7. Never miss a service request.',
    painPoints: [
      { title: 'Emergency Request Overload', description: 'When the AC dies in summer or heat fails in winter, everyone messages at once. Overwhelmed inboxes mean lost customers.' },
      { title: 'After-Hours Emergencies', description: 'HVAC emergencies happen at the worst times. No AC at 2 AM means desperate customers messaging competitors.' },
      { title: 'Seasonal Demand Spikes', description: 'Summer and winter peaks overwhelm your capacity. You can\'t hire for 2 months of chaos.' },
      { title: 'Technician Scheduling', description: 'Coordinating service appointments with parts availability and tech expertise is a daily puzzle.' }
    ],
    useCases: [
      { title: 'Emergency Dispatch', description: 'AI qualifies emergencies (no heat with elderly, AC failure with infants) and notifies on-call technicians instantly.' },
      { title: 'Service Type Routing', description: 'Different problems need different techs. AI routes AC repairs, furnace issues, and duct cleaning appropriately.' },
      { title: 'Maintenance Plan Upsells', description: 'AI educates customers about maintenance plans, capturing leads for your recurring revenue programs.' },
      { title: 'Parts & Availability Check', description: 'For common repairs, AI can check if parts are in stock before scheduling, reducing repeat visits.' }
    ],
    sampleConversation: [
      { role: 'customer', message: "My furnace stopped working and it's 20 degrees outside!" },
      { role: 'ai', message: "I understand the urgency - no heat in freezing weather is a serious situation. Let me get a technician to you quickly. First, are there any elderly people, infants, or anyone with health conditions in the home?" },
      { role: 'customer', message: "Yes, my elderly mother lives with us." },
      { role: 'ai', message: "I'm marking this as a priority emergency. I'm dispatching our on-call technician now. Can you confirm your address? While you wait, make sure space heaters are away from flammables and keep everyone warm. Our tech should arrive within 60-90 minutes." }
    ],
    roiComparison: {
      traditional: { title: 'Dispatch Staff', cost: '$4,000/mo', items: ['After-hours overtime', 'Multiple dispatchers needed', 'Training for seasonality', 'High turnover in peak'] },
      harmonix: { title: 'Harmonix AI', cost: '$99/mo', items: ['24/7/365 coverage', 'Handles unlimited calls', 'Scales with demand', 'Consistent emergency handling'] }
    },
    faqs: [
      { q: 'Can it handle the summer/winter rush?', a: 'Absolutely. AI scales infinitely - whether you get 10 calls or 1,000 calls, every customer gets immediate attention.' },
      { q: 'Does it know our service area?', a: 'Yes. Configure your service area by zip codes, cities, or custom boundaries. AI verifies addresses before booking.' },
      { q: 'What about after-hours emergency fees?', a: 'AI can explain your after-hours rates, get customer approval, and document acceptance before dispatching.' },
      { q: 'Can it schedule maintenance appointments?', a: 'Of course! AI books tune-ups, filter replacements, and seasonal check-ups into your regular schedule.' }
    ],
    cta: 'Start Your AI HVAC Assistant',
    stats: { messagesHandled: '70,000+', avgResponseTime: '4 seconds', customerSatisfaction: '93%' }
  },
  auto: {
    name: 'Auto Repair Shops',
    headline: 'AI Assistant for Auto Repair Shops',
    subheadline: 'Book Service Appointments. Provide Estimates. Never Miss a Customer.',
    icon: Wrench,
    color: 'from-zinc-600 to-zinc-700',
    metaDescription: 'AI assistant for auto repair shops. Handle appointment booking, service estimates, and customer inquiries 24/7.',
    painPoints: [
      { title: 'Missed Messages During Repairs', description: 'Techs are under cars, not checking messages. Every missed inquiry is a lost oil change or brake job.' },
      { title: 'Quote Request Overload', description: 'Customers want instant estimates. But accurate quotes need vehicle info, which takes time to gather.' },
      { title: 'Appointment No-Shows', description: 'Bay time is money. No-shows and late arrivals disrupt your entire day\'s schedule.' },
      { title: 'Parts Availability Questions', description: 'Customers ask about specific repairs before committing. Can you do it? How long? What\'s it cost?' }
    ],
    useCases: [
      { title: 'Service Appointment Booking', description: 'AI books oil changes, brake jobs, and inspections with accurate time estimates for each service type.' },
      { title: 'Preliminary Estimate Generation', description: 'For common services, AI provides ballpark estimates. Complex repairs get flagged for technician callback.' },
      { title: 'Vehicle Information Capture', description: 'AI collects year, make, model, mileage, and symptoms before the appointment - your tech arrives prepared.' },
      { title: 'Service Reminder Automation', description: 'Based on mileage and service history, AI reminds customers when maintenance is due.' }
    ],
    sampleConversation: [
      { role: 'customer', message: "My check engine light came on. How much to diagnose it?" },
      { role: 'ai', message: "Check engine lights can indicate many different issues, so a diagnostic is the first step. Our diagnostic fee is $89, which includes a full code scan and visual inspection. If you proceed with repairs, we apply that $89 to your total. What year and model is your vehicle?" },
      { role: 'customer', message: "2019 Honda Civic" },
      { role: 'ai', message: "Great, the 2019 Civic is a vehicle we work on frequently. I can get you in tomorrow at 9 AM or 2 PM for the diagnostic. Which time works better? Also, when did the light first come on, and have you noticed any performance changes?" }
    ],
    roiComparison: {
      traditional: { title: 'Service Advisor', cost: '$3,800/mo', items: ['$18-24/hr wages', 'Benefits and training', 'Limited to shop hours', 'Splits time with in-person'] },
      harmonix: { title: 'Harmonix AI', cost: '$79/mo', items: ['24/7 appointment booking', 'Consistent information gathering', 'Never puts customers on hold', 'Automated reminders'] }
    },
    faqs: [
      { q: 'Can it quote specific repairs?', a: 'For standard services (oil changes, brakes, tires), AI provides accurate estimates. Complex repairs are flagged for your team to quote.' },
      { q: 'Does it work with my shop management software?', a: 'Harmonix integrates with Shop-Ware, Mitchell 1, and most shop management systems for scheduling and customer records.' },
      { q: 'What about warranty and recalls?', a: 'AI can be trained on your warranty policies and common manufacturer recalls, directing customers appropriately.' },
      { q: 'Can it handle fleet accounts?', a: 'Yes. AI recognizes fleet customers, applies negotiated rates, and routes through your fleet management workflow.' }
    ],
    cta: 'Start Your AI Auto Shop Assistant',
    stats: { messagesHandled: '35,000+', avgResponseTime: '4 seconds', customerSatisfaction: '92%' }
  },
  salons: {
    name: 'Salons & Spas',
    headline: 'AI Assistant for Salons & Spas',
    subheadline: 'Book Appointments 24/7. Reduce No-Shows. Focus on Your Clients.',
    icon: Users,
    color: 'from-pink-500 to-rose-500',
    metaDescription: 'AI assistant for salons and spas. Handle appointment booking, service inquiries, and client management 24/7.',
    painPoints: [
      { title: 'Message Interruptions', description: 'Every message notification during a service interrupts your work and client experience. But unanswered messages mean lost bookings.' },
      { title: 'After-Hours Booking Loss', description: 'Clients browse for salons at night. If they can\'t book immediately, they go to whoever responds.' },
      { title: 'Double-Booking Nightmares', description: 'Manual booking leads to overlaps, especially when multiple stylists share a schedule.' },
      { title: 'No-Shows and Cancellations', description: 'Last-minute cancellations leave empty chairs. No-shows cost you potential revenue.' }
    ],
    useCases: [
      { title: '24/7 Online Booking', description: 'Clients book haircuts, colors, facials, and spa services anytime. AI knows service durations and stylist availability.' },
      { title: 'Service Consultation', description: 'AI helps clients choose services, explains pricing, and recommends add-ons based on their needs.' },
      { title: 'Stylist Matching', description: 'Based on service type and preferences, AI books clients with the right stylist or esthetician.' },
      { title: 'Reminder & Rebooking', description: 'Automated reminders reduce no-shows. After appointments, AI prompts rebooking.' }
    ],
    sampleConversation: [
      { role: 'customer', message: "I need to book a haircut and color for Saturday" },
      { role: 'ai', message: "I'd love to help you book that! For your color service, are you looking for a full color, highlights/lowlights, or a balayage? This helps me match you with the right stylist and time slot." },
      { role: 'customer', message: "Highlights, probably partial" },
      { role: 'ai', message: "A partial highlight with a haircut typically takes about 2.5 hours. I have availability with Jessica at 10 AM or with Maria at 1 PM on Saturday. Both are excellent with highlights. Which do you prefer?" }
    ],
    roiComparison: {
      traditional: { title: 'Reception Staff', cost: '$2,800/mo', items: ['$15-18/hr wages', 'Only works salon hours', 'Splits duties (checkout, etc.)', 'Vacation coverage needed'] },
      harmonix: { title: 'Harmonix AI', cost: '$79/mo', items: ['24/7 booking availability', 'Handles unlimited inquiries', 'Never puts clients on hold', 'Consistent upselling'] }
    },
    faqs: [
      { q: 'Can it book for multiple stylists?', a: 'Yes! Configure availability and services for each stylist. AI knows who does what and when they\'re available.' },
      { q: 'Does it handle add-on services?', a: 'Absolutely. AI can suggest relevant add-ons (deep conditioning with color, express facial with massage) and book them.' },
      { q: 'What about cancellation policies?', a: 'AI explains your cancellation policy, collects card information for no-show fees if needed, and sends confirmation reminders.' },
      { q: 'Can clients request specific stylists?', a: 'Of course! Clients can request their favorite stylist, and AI shows only that person\'s availability.' }
    ],
    cta: 'Start Your AI Salon Assistant',
    stats: { messagesHandled: '40,000+', avgResponseTime: '3 seconds', customerSatisfaction: '96%' }
  },
  fitness: {
    name: 'Fitness & Gyms',
    headline: 'AI Assistant for Fitness Centers',
    subheadline: 'Convert More Members. Handle Inquiries 24/7. Scale Your Gym.',
    icon: Users,
    color: 'from-green-500 to-emerald-600',
    metaDescription: 'AI assistant for gyms and fitness centers. Handle membership inquiries, class bookings, and tours 24/7.',
    painPoints: [
      { title: 'Lost Lead Follow-Up', description: 'Web leads expect quick responses. By the time you call back, they\'ve signed up elsewhere.' },
      { title: 'After-Hours Inquiries', description: 'People research gyms after work. If you can\'t answer at 9 PM, you lose them.' },
      { title: 'Class Booking Chaos', description: 'Popular classes fill fast. Managing waitlists and cancellations manually is a nightmare.' },
      { title: 'Membership Question Overload', description: 'Staff answers the same pricing and amenity questions all day. There has to be a better way.' }
    ],
    useCases: [
      { title: 'Membership Sales Support', description: 'AI explains membership tiers, answers FAQs, and schedules tours - capturing leads 24/7.' },
      { title: 'Class Booking & Waitlists', description: 'Members book classes, get waitlisted, and receive notifications when spots open.' },
      { title: 'Personal Training Inquiries', description: 'AI qualifies PT leads by goals, availability, and budget, then connects them with trainers.' },
      { title: 'Facility Information', description: 'Hours, amenities, policies, and directions - AI handles all the common questions.' }
    ],
    sampleConversation: [
      { role: 'customer', message: "What membership options do you have? I'm mainly interested in group classes." },
      { role: 'ai', message: "Great question! We have three membership levels. Our 'Class Pass' at $79/month includes unlimited group fitness classes - perfect for your interest. We also have 'Full Access' at $99/month with classes plus all gym equipment, and 'Premium' at $149/month which adds personal training sessions. Would you like to schedule a tour to see our class studios?" },
      { role: 'customer', message: "Yes, but I can only come after 6pm" },
      { role: 'ai', message: "No problem! I have evening tour slots available Tuesday at 6:30 PM or Thursday at 7 PM. During the tour, you can see a class in action - we have Spin at 6:30 Tuesday and HIIT at 7 Thursday. Which works better for you?" }
    ],
    roiComparison: {
      traditional: { title: 'Front Desk Staff', cost: '$3,200/mo', items: ['$16-20/hr wages', 'Split between tasks', 'No after-hours coverage', 'Inconsistent sales pitch'] },
      harmonix: { title: 'Harmonix AI', cost: '$99/mo', items: ['24/7 lead capture', 'Consistent messaging', 'Class booking automation', 'Tour scheduling'] }
    },
    faqs: [
      { q: 'Can it integrate with our member management system?', a: 'Harmonix works with MindBody, ClubReady, ABC Fitness, and most gym management platforms.' },
      { q: 'Does it help with membership sales?', a: 'Yes! AI is trained on your membership options and can explain benefits, answer objections, and schedule tours or trial classes.' },
      { q: 'What about personal training upsells?', a: 'AI qualifies PT leads and books complimentary assessments with trainers, helping convert members to PT clients.' },
      { q: 'Can members book classes through it?', a: 'Absolutely. Members can check class schedules, book spots, join waitlists, and get notified of cancellations.' }
    ],
    cta: 'Start Your AI Fitness Assistant',
    stats: { messagesHandled: '55,000+', avgResponseTime: '3 seconds', customerSatisfaction: '94%' }
  }
};

const IndustryPage = () => {
  // Extract industry ID from URL path (e.g., /ai-for-plumbers -> plumbers)
  const location = window.location.pathname;
  const industryId = location.replace('/ai-for-', '');
  const industry = industriesData[industryId];

  // A/B test the hero CTA on every industry page. Variant B tries a
  // higher-intent CTA and a benefit-led subheading to see if intent-heavy
  // copy converts better than the industry-authored default.
  const { variant: heroVariant, trackConversion } = useSeoAbVariant(
    `industry_hero_${industryId || 'unknown'}`,
    ['a', 'b']
  );

  if (!industry) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Industry Not Found</h1>
          <Link to="/industries">
            <Button>View All Industries</Button>
          </Link>
        </div>
      </div>
    );
  }

  const Icon = industry.icon;

  // Per-industry SEO: title, description, keywords and OG tags pulled from
  // the page's own data so every industry route surfaces a unique snippet to
  // Google / social shares (was sharing one generic title across all routes).
  const firstPain = industry.painPoints?.[0]?.title || '';
  const seoTitle = `${industry.headline} | Harmonix AI Employee`;
  const seoDescription = `${industry.subheadline} Harmonix's AI handles ${
    (industry.useCases || []).slice(0, 3).map(u => u.title.toLowerCase()).join(', ')
  } for ${industry.name.toLowerCase()}. ${firstPain ? 'Stop losing leads to ' + firstPain.toLowerCase() + '.' : ''}`.trim();
  const seoKeywords = [
    `AI for ${industry.name.toLowerCase()}`,
    `${industry.name} automation`,
    `AI assistant ${industry.name.toLowerCase()}`,
    `virtual receptionist ${industry.name.toLowerCase()}`,
    ...(industry.useCases || []).map(u => u.title.toLowerCase()),
  ].slice(0, 12).join(', ');
  const seoImage = INDUSTRY_IMAGES[industryId] || INDUSTRY_IMAGES.plumbers;
  const canonicalUrl = `https://harmonix.info/ai-for-${industryId}`;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={industry.subheadline} />
        <meta property="og:image" content={seoImage} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={industry.headline} />
        <meta name="twitter:description" content={industry.subheadline} />
        <meta name="twitter:image" content={seoImage} />

        {/* Service schema — distinguishes each industry as a separate offering. */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": industry.headline,
            "serviceType": `AI Assistant for ${industry.name}`,
            "provider": { "@type": "Organization", "name": "Harmonix" },
            "areaServed": { "@type": "Country", "name": "United States" },
            "description": industry.subheadline,
            "url": canonicalUrl,
            "image": seoImage,
            "audience": { "@type": "BusinessAudience", "audienceType": industry.name },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": `Harmonix features for ${industry.name.toLowerCase()}`,
              "itemListElement": (industry.useCases || []).map((u, i) => ({
                "@type": "Offer",
                "position": i + 1,
                "itemOffered": {
                  "@type": "Service",
                  "name": u.title,
                  "description": u.description,
                },
              })),
            },
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <PublicHeader />

      {/* Hero Section with Image */}
      <section className="relative overflow-hidden">
        <div className={`py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br ${industry.color} text-white`}>
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-6">
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" data-testid={`hero-headline-${heroVariant}`}>
                  {industry.headline}
                </h1>
                <p className="text-lg md:text-xl opacity-90 max-w-xl mb-8">
                  {heroVariant === 'b'
                    ? `Answer every call, book more jobs, and never lose a lead — even after hours. Trained on your business, live in 3 minutes.`
                    : industry.subheadline}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/login?signup=true" onClick={() => trackConversion('cta_click', { position: 'hero' })}>
                    <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 px-8" data-testid={`hero-cta-${heroVariant}`}>
                      {heroVariant === 'b' ? 'Start free — no credit card' : industry.cta}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
              <div className="hidden lg:block">
                <img 
                  src={INDUSTRY_IMAGES[industryId] || INDUSTRY_IMAGES.plumbers} 
                  alt={industry.name}
                  className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3] border-4 border-white/20"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-slate-50 border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-3xl font-bold text-slate-900">{industry.stats.messagesHandled}</p>
              <p className="text-sm text-slate-600">Messages Handled</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{industry.stats.avgResponseTime}</p>
              <p className="text-sm text-slate-600">Avg Response</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{industry.stats.customerSatisfaction}</p>
              <p className="text-sm text-slate-600">Satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              The Challenges {industry.name} Face
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Sound familiar? You're not alone. Here's what keeps business owners up at night.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {industry.painPoints.map((pain, idx) => (
              <motion.div
                key={`pain-${pain.title}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                className="bg-red-50 border border-red-100 rounded-xl p-6"
              >
                <h3 className="text-xl font-bold text-slate-900 mb-2">{pain.title}</h3>
                <p className="text-slate-600">{pain.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How Harmonix Helps
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Purpose-built AI features for {industry.name.toLowerCase()}.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {industry.useCases.map((useCase, idx) => (
              <motion.div
                key={`uc-${useCase.title}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${industry.color} flex items-center justify-center mb-4`}>
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{useCase.title}</h3>
                <p className="text-slate-600">{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Conversation */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              See It In Action
            </h2>
            <p className="text-lg text-slate-600">
              A real conversation your AI assistant might have.
            </p>
          </div>
          <Card className="border-slate-200 shadow-xl">
            <div className={`p-4 bg-gradient-to-r ${industry.color} rounded-t-lg flex items-center gap-3`}>
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold">AI Assistant</p>
                <p className="text-white/70 text-sm">Trained for {industry.name}</p>
              </div>
            </div>
            <CardContent className="p-6 space-y-4">
              {industry.sampleConversation.map((msg, idx) => (
                <div key={`msg-${idx}-${msg.role}`} className={`flex ${msg.role === 'customer' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] p-4 rounded-xl ${
                    msg.role === 'customer'
                      ? 'bg-slate-500 text-white rounded-br-sm'
                      : 'bg-slate-100 text-slate-700 rounded-bl-sm'
                  }`}>
                    {msg.message}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ROI Comparison */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              The Real Cost Comparison
            </h2>
            <p className="text-lg text-slate-600">
              See how much you save with AI vs. traditional staffing.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">{industry.roiComparison.traditional.title}</h3>
                <p className="text-3xl font-bold text-red-600 mb-4">{industry.roiComparison.traditional.cost}</p>
                <ul className="space-y-2">
                  {industry.roiComparison.traditional.items.map((item) => (
                    <li key={`trad-${item}`} className="flex items-start gap-2 text-slate-600">
                      <span className="text-red-500 mt-1">×</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50 ring-2 ring-emerald-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold text-slate-900">{industry.roiComparison.harmonix.title}</h3>
                  <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full">RECOMMENDED</span>
                </div>
                <p className="text-3xl font-bold text-emerald-600 mb-4">{industry.roiComparison.harmonix.cost}</p>
                <ul className="space-y-2">
                  {industry.roiComparison.harmonix.items.map((item) => (
                    <li key={`harm-${item}`} className="flex items-start gap-2 text-slate-600">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section with Schema */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600">
              Common questions from {industry.name.toLowerCase()}.
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {industry.faqs.map((faq) => (
              <AccordionItem key={faq.q} value={`faq-${faq.q.slice(0, 40)}`} className="bg-white border border-slate-200 rounded-xl px-6">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          {/* FAQ Schema for SEO */}
          {/*
            False-positive in automated scanners: the JSON-LD payload is built
            from hardcoded industry data only (no user input). We still escape
            the `</` sequence — the single XSS vector inside a JSON-LD <script>
            block — as defense-in-depth.
          */}
          <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": industry.faqs.map(faq => ({
                "@type": "Question",
                "name": faq.q,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.a
                }
              }))
            }).replace(/</g, '\\u003c')
          }} />
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r ${industry.color}`}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your {industry.name}?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Start your free 14-day trial. No credit card required.
          </p>
          <Link to="/login?signup=true">
            <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 px-8">
              {industry.cta}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
      </div>
    </>
  );
};

export default IndustryPage;
